import { useCallback, useEffect, useMemo, useState } from "react";
import {
  buildDayPanel,
  dayPanelEntryKey,
  type Block,
  type ComposedBlock,
  type DayPanelEntry,
} from "@heraion/shared";

import { formMatchesBlock } from "./composeBlocks";
import type { ComposerForm } from "./useComposer";

// Ramp-edit state machine (✎ on a multi-week day-panel entry), split out
// of useComposer (500-line budget). The entry is tracked by its stable
// key, NOT by block references — every commit re-derives the block set,
// so the segments recompute fresh and survive their own updates (incl.
// segments merging when an edit makes two week-sets equal, and splitting
// when a single-week edit makes them differ). Two granularities: content
// segments (perWeek=false, idx over rampBlocks) or single weeks
// (perWeek=true, idx over rampWeeks) — the per-week view is how a
// uniform segment gets "broken" into a ramp (Antenore 2026-06-11).
export function useRampEdit({
  blocks,
  form,
  setForm,
  startEdit,
  weekCount,
}: {
  blocks: Block[];
  form: ComposerForm;
  setForm: (f: ComposerForm) => void;
  startEdit: (block: ComposedBlock, editingDay?: number | null) => void;
  weekCount: number;
}) {
  const [ramp, setRamp] = useState<{
    dayIdx: number;
    key: string;
    idx: number;
    perWeek: boolean;
  } | null>(null);

  const rampBlocks = useMemo(() => {
    if (!ramp) return null;
    // No kind labels passed on purpose: this is a lookup, not a render —
    // only `entry.blocks` is read, never the localized `summary` string.
    const entry = buildDayPanel(blocks, ramp.dayIdx).find(
      (e): e is Extract<DayPanelEntry, { kind: "composed" }> =>
        e.kind === "composed" &&
        dayPanelEntryKey(e.blocks[0]!, ramp.dayIdx) === ramp.key,
    );
    return entry ? entry.blocks : null;
  }, [blocks, ramp]);

  const rampWeeks = useMemo(
    () =>
      rampBlocks
        ? [...new Set(rampBlocks.flatMap((b) => b.weeks))].sort((a, b) => a - b)
        : null,
    [rampBlocks],
  );

  // Week being edited in per-week mode (commit narrows the owning block
  // to its other weeks and applies this week's content separately).
  const [weekEdit, setWeekEdit] = useState<number | null>(null);

  // Uncommitted edits in block-edit mode — the strip refuses to switch
  // segment while true (the switch reloads the form). In per-week mode
  // the weeks differ from the owning block BY DESIGN, so only the
  // members (and day targets) count.
  const formDirty = useMemo(() => {
    if (form.editing === null) return false;
    // CD-1 — a day-scoped edit loads pinned to {editingDay}, so the clean
    // baseline is that day, not the block's full day spread (else the form
    // reads dirty the instant a fused multi-day block opens, locking the strip).
    const baselineDays =
      form.editingDay != null ? [form.editingDay] : undefined;
    if (weekEdit !== null) {
      // Pass the block's own weeks so only members + days are compared.
      return !formMatchesBlock(
        form.members,
        [...form.days],
        form.editing.weeks,
        form.editing,
        baselineDays,
      );
    }
    return !formMatchesBlock(
      form.members,
      [...form.days],
      [...form.weeks],
      form.editing,
      baselineDays,
    );
  }, [form, weekEdit]);

  // Load the virtual single-week form: the owning segment's content,
  // weeks pinned to {week}. UNCOVERED weeks load too (Antenore
  // 2026-06-11: "se non posso aggiungere il week in più, mi serve a
  // poco") — seeded from the nearest covered week below (else the first
  // segment); commit's narrow step is a no-op there and applyBlock just
  // adds the week to the same slot.
  const startWeekEdit = useCallback(
    (blocksForEntry: ComposedBlock[], week: number, dayIdx: number) => {
      const owner =
        blocksForEntry.find((b) => b.weeks.includes(week)) ??
        [...blocksForEntry]
          .filter((b) => Math.min(...b.weeks) < week)
          .sort((a, b) => Math.max(...b.weeks) - Math.max(...a.weeks))[0] ??
        blocksForEntry[0];
      if (!owner) return;
      setWeekEdit(week);
      setForm({
        members: owner.members.map((m) => ({
          ...m,
          setRows: m.setRows.map((r) => ({ ...r })),
        })),
        grouped: owner.members.length > 1,
        // CD-1 — day-scoped: the per-week edit stays on the day it opened from.
        days: new Set([dayIdx]),
        weeks: new Set([week]),
        editing: owner,
        editingDay: dayIdx,
      });
    },
    [setForm],
  );

  // ✎ from the day panel. Single-week entry → plain block edit.
  // Multi-week entry → ramp mode (segment strip + per-week toggle), so a
  // uniform 6-week block is one tap away from week-level surgery.
  const startEditEntry = useCallback(
    (entry: Extract<DayPanelEntry, { kind: "composed" }>, dayIdx: number) => {
      const first = entry.blocks[0];
      if (!first) return;
      setWeekEdit(null);
      if (entry.blocks.length === 1 && entry.weeks.length <= 1) {
        setRamp(null);
        startEdit(first, dayIdx);
        return;
      }
      setRamp({ dayIdx, key: dayPanelEntryKey(first, dayIdx), idx: 0, perWeek: false });
      startEdit(first, dayIdx);
    },
    [startEdit],
  );

  // In per-week mode `idx` IS the week number (0-based) over the whole
  // cycle — uncovered weeks are selectable (they ADD the week on
  // Update). In segment mode it indexes rampBlocks.
  const selectRampSegment = useCallback(
    (idx: number) => {
      if (formDirty || !ramp) return;
      if (ramp.perWeek) {
        if (!rampBlocks || idx < 0 || idx >= weekCount) return;
        setRamp({ ...ramp, idx });
        startWeekEdit(rampBlocks, idx, ramp.dayIdx);
        return;
      }
      const target = rampBlocks?.[idx];
      if (!target) return;
      setRamp({ ...ramp, idx });
      startEdit(target, ramp.dayIdx);
    },
    [ramp, rampBlocks, weekCount, formDirty, startEdit, startWeekEdit],
  );

  // Toggle segment-granularity ↔ week-granularity. Blocked while dirty
  // (the switch reloads the form).
  const setRampPerWeek = useCallback(
    (perWeek: boolean) => {
      if (!ramp || !rampBlocks || formDirty || perWeek === ramp.perWeek) return;
      setWeekEdit(null);
      if (perWeek) {
        const week = rampWeeks?.[0] ?? 0;
        setRamp({ ...ramp, perWeek, idx: week });
        startWeekEdit(rampBlocks, week, ramp.dayIdx);
      } else {
        setRamp({ ...ramp, perWeek, idx: 0 });
        startEdit(rampBlocks[0]!, ramp.dayIdx);
      }
    },
    [ramp, rampBlocks, rampWeeks, formDirty, startEdit, startWeekEdit],
  );

  // After an Update in ramp mode the draft re-derives and commit blanks
  // the form: reload the current segment/week so the user steps on
  // instead of falling out of the edit. Exits cleanly if the whole entry
  // disappeared.
  useEffect(() => {
    if (!ramp) return;
    if (!rampBlocks || rampBlocks.length === 0) {
      setRamp(null);
      setWeekEdit(null);
      return;
    }
    if (form.editing === null) {
      if (ramp.perWeek) {
        const idx = Math.min(ramp.idx, weekCount - 1);
        if (idx !== ramp.idx) setRamp({ ...ramp, idx });
        startWeekEdit(rampBlocks, idx, ramp.dayIdx);
        return;
      }
      const idx = Math.min(ramp.idx, rampBlocks.length - 1);
      if (idx !== ramp.idx) setRamp({ ...ramp, idx });
      startEdit(rampBlocks[idx]!, ramp.dayIdx);
    }
  }, [ramp, rampBlocks, weekCount, form.editing, startEdit, startWeekEdit]);

  const clearRamp = useCallback(() => {
    setRamp(null);
    setWeekEdit(null);
  }, []);

  return {
    ramp,
    rampBlocks,
    rampWeeks,
    weekEdit,
    formDirty,
    startEditEntry,
    selectRampSegment,
    setRampPerWeek,
    clearRamp,
  };
}
